import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { PieChart as PieChartIcon, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { toast } from '../../lib/safeToast';

const COLORS = { total_registered: '#64748b', onboarding_started: '#3b82f6', submitted: '#0B6445', in_review: '#f59e0b', approved: '#22c55e', rejected: '#ef4444' };
const LABELS = { total_registered: 'Registered', onboarding_started: 'Onboarding Started', submitted: 'Submitted', in_review: 'In Review', approved: 'Approved', rejected: 'Rejected' };

export default function BackOfficeAnalyticsKyc() {
  const { api } = useAuth();
  const { isLive } = useEnvironment();
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/analytics/kyc-funnel');
      setFunnel(res.data?.data || null);
    } catch { toast.error('Failed to load KYC funnel'); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData, isLive]);

  const chartData = funnel ? Object.entries(funnel).map(([key, value]) => ({ name: LABELS[key] || key, value, color: COLORS[key] || '#94a3b8' })) : [];

  return (
    <BackOfficeLayout>
      <div className="space-y-8">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><PieChartIcon className="w-7 h-7 text-[#0B6445]" />KYC Funnel</h1>
            <p className="text-[#0E1B16]/70 mt-2">KYC / KYB conversion funnel and status breakdown.</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : !funnel ? (
          <Card className="border-[#DCE4E0] shadow-sm"><CardContent className="pt-6"><p className="text-center py-12 text-[#0E1B16]/60">No data available.</p></CardContent></Card>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
              {chartData.map((d) => (
                <Card key={d.name} className="border-[#DCE4E0] shadow-sm">
                  <CardContent className="p-5 text-center">
                    <p className="text-2xl font-bold" style={{ color: d.color }}>{d.value}</p>
                    <p className="text-xs text-slate-500 mt-2">{d.name}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="border-[#DCE4E0] shadow-sm mt-10">
              <CardHeader className="pb-3 pt-6"><CardTitle className="text-base text-[#073B2E]">Funnel Breakdown</CardTitle></CardHeader>
              <CardContent className="pb-6">
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ left: 120 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={110} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} name="Count">
                        {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </BackOfficeLayout>
  );
}
